import { createContext, useContext, useState } from 'react';

import {
  TimeAttendanceMarkStatus,
  TimeAttendanceMarkType,
} from '@/generated/graphql/types';

import { useWidgetClockContext } from '../context-clock';
import { useRecordAttendanceMark } from '../hooks/use-record-attendance-mark';
import type { TMarkResult } from '../hooks/use-record-attendance-mark/use-record-attendance-mark';
import { type TKeys, useTranslation } from '../i18n';
import { AftRecordMarkDialog, BeforeRecordMarkDialog } from './dialogs';

// ─── Before-dialog variant → i18n key mapping ───────────────────────

type TBeforeDialogVariant = 'no-shift' | 'early-entry' | 'early-exit';

const BEFORE_DIALOG_KEYS: Record<
  TBeforeDialogVariant,
  { titleKey: TKeys; descKey: TKeys }
> = {
  'no-shift': {
    titleKey: 'confirm_mark_no_shift_title',
    descKey: 'confirm_mark_no_shift_description',
  },
  'early-entry': {
    titleKey: 'confirm_mark_early_entry_title',
    descKey: 'confirm_mark_early_entry_description',
  },
  'early-exit': {
    titleKey: 'confirm_mark_early_exit_title',
    descKey: 'confirm_mark_early_exit_description',
  },
};

// ─── Mark type → event label i18n key ────────────────────────────────

const MARK_EVENT_KEY: Record<TimeAttendanceMarkType, TKeys> = {
  [TimeAttendanceMarkType.Entry]: 'mark_event_entry',
  [TimeAttendanceMarkType.BreakStart]: 'mark_event_break_start',
  [TimeAttendanceMarkType.BreakEnd]: 'mark_event_break_end',
  [TimeAttendanceMarkType.Exit]: 'mark_event_exit',
};

// ─── After-dialog display derivation ─────────────────────────────────
// Pure function that derives all display props from stored state.
// Called during render so `t` is always the current reference.

type TAfterDialogState = {
  open: boolean;
  result?: TMarkResult;
  markType?: TimeAttendanceMarkType;
};

type TTranslateFn = ReturnType<typeof useTranslation>['t'];

const deriveAfterDialogProps = (
  state: TAfterDialogState,
  shiftName: string | undefined,
  t: TTranslateFn,
) => {
  const { result, markType } = state;

  if (!result || !markType) {
    return { variant: 'success' as const };
  }

  const resolvedMarkType = result.success ? result.mark.mark_type : markType;
  const eventLabel = t(MARK_EVENT_KEY[resolvedMarkType]);

  const markInfo = {
    markedAt: result.success ? result.mark.marked_at : new Date().toISOString(),
    shiftName: shiftName ?? t('registered_mark_modal_row_without_shift'),
    markEventLabel: eventLabel,
    markExpectedAt: '',
  };

  // ── Error path ──
  if (!result.success) {
    const isDuplicate = result.errorCode === 'MARK_ALREADY_REGISTERED';

    return {
      variant: 'alert' as const,
      bannerMessage: isDuplicate
        ? t('registered_mark_modal_error_duplicate')
        : t('registered_mark_modal_error_default'),
      markInfo,
    };
  }

  const { status } = result.mark;
  const eventLabelLower = eventLabel.toLowerCase();

  // LATE → "después del horario establecido"
  if (status === TimeAttendanceMarkStatus.Late) {
    return {
      variant: 'warning' as const,
      bannerMessage: t('registered_mark_modal_delay_banner_message', {
        interpolation: { markName: eventLabelLower },
      }),
      markInfo,
    };
  }

  // EARLY → "antes del horario establecido"
  if (status === TimeAttendanceMarkStatus.Early) {
    return {
      variant: 'warning' as const,
      bannerMessage: t('registered_mark_modal_early_banner_message', {
        interpolation: { markName: eventLabelLower },
      }),
      markInfo,
    };
  }

  // OUT_OF_SCHEDULE → direction inferred from mark type:
  // ENTRY/BREAK_START arrived before tolerance window → "antes"
  // EXIT/BREAK_END left after tolerance window → "después"
  if (status === TimeAttendanceMarkStatus.OutOfSchedule) {
    const isLateDirection =
      resolvedMarkType === TimeAttendanceMarkType.Exit ||
      resolvedMarkType === TimeAttendanceMarkType.BreakEnd;

    const messageKey: TKeys = isLateDirection
      ? 'registered_mark_modal_delay_banner_message'
      : 'registered_mark_modal_early_banner_message';

    return {
      variant: 'warning' as const,
      bannerMessage: t(messageKey, {
        interpolation: { markName: eventLabelLower },
      }),
      markInfo,
    };
  }

  // ON_TIME, ACCEPTABLE, REGISTERED → success
  return {
    variant: 'success' as const,
    bannerMessage: t('registered_mark_modal_success_banner_message', {
      interpolation: { markName: eventLabelLower },
    }),
    markInfo,
  };
};

// ─── Context ─────────────────────────────────────────────────────────

type TContextMarkAttendance = {
  markAttendance: (markType: TimeAttendanceMarkType) => void;
  mutationLoading: boolean;
};

const ContextMarkAttendance = createContext<TContextMarkAttendance | undefined>(
  undefined,
);

/**
 * Orchestrates the full mark registration flow:
 * button click → pre-condition check → confirmation dialog (if needed) → mutation → result dialog.
 *
 * All business rules live here. The dialog components are purely presentational.
 */
export const ContextMarkAttendanceProvider = ({
  children,
  timeZone,
}: {
  children: React.ReactNode;
  timeZone: string;
}) => {
  const { t } = useTranslation();
  const { data, refetch } = useWidgetClockContext();
  const { recordMark, loading: mutationLoading } = useRecordAttendanceMark({
    timeZone,
  });

  const [pendingMarkType, setPendingMarkType] = useState<
    TimeAttendanceMarkType | undefined
  >();

  // Before-mutation dialog: only stores which variant to show.
  // Translated strings are derived during render from the variant.
  const [beforeDialog, setBeforeDialog] = useState<{
    open: boolean;
    variant: TBeforeDialogVariant;
  }>({ open: false, variant: 'no-shift' });

  // After-mutation dialog: stores the raw result + mark type.
  // Banner variant, message, and markInfo are derived during render.
  const [afterDialog, setAfterDialog] = useState<TAfterDialogState>({
    open: false,
  });

  const closeBeforeDialog = () => {
    setBeforeDialog(prev => ({ ...prev, open: false }));
    setPendingMarkType(undefined);
  };

  const closeAfterDialog = () => {
    setAfterDialog(prev => ({ ...prev, open: false }));
  };

  const executeMark = async (markType: TimeAttendanceMarkType) => {
    const result = await recordMark(markType);
    setAfterDialog({ open: true, result, markType });

    if (result.success) {
      refetch();
    }
  };

  const confirmAndMark = () => {
    if (pendingMarkType) {
      setBeforeDialog(prev => ({ ...prev, open: false }));
      executeMark(pendingMarkType);
    }
  };

  const retryMark = () => {
    if (pendingMarkType) {
      setAfterDialog(prev => ({ ...prev, open: false }));
      executeMark(pendingMarkType);
    }
  };

  // ── Pre-condition checks (client-side only, backend is permissive) ──
  const markAttendance = (markType: TimeAttendanceMarkType) => {
    if (mutationLoading) {
      return;
    }

    setPendingMarkType(markType);
    const shiftContext = data?.shift_context;

    // Rule 1: No shift assigned (only for entry — once entry is confirmed,
    // subsequent marks don't need re-confirmation)
    if (!shiftContext && markType === TimeAttendanceMarkType.Entry) {
      setBeforeDialog({ open: true, variant: 'no-shift' });
      return;
    }

    // Rules 2 & 3 only apply when a shift exists
    if (shiftContext) {
      // Rule 2: Entry before shift starts
      if (
        markType === TimeAttendanceMarkType.Entry &&
        shiftContext.effective_shift_start
      ) {
        const shiftStart = new Date(
          shiftContext.effective_shift_start,
        ).getTime();

        if (Date.now() < shiftStart) {
          setBeforeDialog({ open: true, variant: 'early-entry' });
          return;
        }
      }

      // Rule 3: Exit before shift ends
      if (markType === TimeAttendanceMarkType.Exit && shiftContext.exit_time) {
        const exitTime = new Date(shiftContext.exit_time).getTime();

        if (Date.now() < exitTime) {
          setBeforeDialog({ open: true, variant: 'early-exit' });
          return;
        }
      }
    }

    // Rule 4: No pre-condition triggered → execute mutation directly
    executeMark(markType);
  };

  // Derive after-dialog display props from stored state (pure, runs at render time)
  const afterDisplayProps = deriveAfterDialogProps(
    afterDialog,
    data?.shift_context?.shift_name,
    t,
  );

  return (
    <ContextMarkAttendance.Provider value={{ markAttendance, mutationLoading }}>
      {children}

      <AftRecordMarkDialog
        open={afterDialog.open}
        variant={afterDisplayProps.variant}
        bannerMessage={afterDisplayProps.bannerMessage}
        markInfo={afterDisplayProps.markInfo}
        onOpenChange={closeAfterDialog}
        onRetry={retryMark}
        retryLoading={mutationLoading}
        timeZone={timeZone}
      />

      <BeforeRecordMarkDialog
        open={beforeDialog.open}
        onOpenChange={closeBeforeDialog}
        title={t(BEFORE_DIALOG_KEYS[beforeDialog.variant].titleKey)}
        description={t(BEFORE_DIALOG_KEYS[beforeDialog.variant].descKey)}
        onConfirm={confirmAndMark}
        loading={mutationLoading}
      />
    </ContextMarkAttendance.Provider>
  );
};

export const useContextMarkAttendance = () => {
  const context = useContext(ContextMarkAttendance);

  if (!context) {
    throw new Error(
      'useContextMarkAttendance must be used within a ContextMarkAttendanceProvider',
    );
  }

  return context;
};
